
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryOptimization, getCachedQuery, cacheQuery } from '@/utils/database/connectionPooling';
import { LeadStatus, LeadPriority } from '@/components/leads/types';

interface LeadFilters {
  status?: LeadStatus;
  priority?: LeadPriority;
  page?: number;
  pageSize?: number;
}

// Type guards to validate filter values
const isValidLeadStatus = (status: string): status is LeadStatus => {
  return ['new', 'contacted', 'meeting', 'proposal', 'negotiating', 'won', 'lost', 'onhold'].includes(status);
};

const isValidLeadPriority = (priority: string): priority is LeadPriority => {
  return ['low', 'medium', 'high', 'urgent'].includes(priority);
};

export const useOptimizedLeads = (filters: LeadFilters = {}) => {
  const { page = 1, pageSize = 20, status, priority } = filters;
  
  return useQuery({
    queryKey: ['leads', 'optimized', page, pageSize, status, priority],
    queryFn: async () => {
      const cacheKey = `leads-${page}-${pageSize}-${status}-${priority}`;
      
      // Check cache first
      const cached = getCachedQuery(cacheKey);
      if (cached) {
        return cached;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Authentication required');

      // Build optimized query
      let query = supabase
        .from('lead_master')
        .select(`
          id, name, status, priority, lead_score, industry, website,
          next_contact_at, updated_at, geographic_area
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      // Apply filters with type validation
      if (status && isValidLeadStatus(status)) {
        query = query.eq('status', status);
      }
      if (priority && isValidLeadPriority(priority)) {
        query = query.eq('priority', priority);
      }

      // Apply pagination
      query = queryOptimization.getPaginatedQuery(query, page, pageSize);

      const { data, error } = await query;
      if (error) throw error;

      // Cache the results
      cacheQuery(cacheKey, data, 120000); // 2 minutes cache
      
      return data || [];
    },
    staleTime: 120000, // 2 minutes
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Count query optimization
export const useLeadsCount = (filters: Omit<LeadFilters, 'page' | 'pageSize'> = {}) => {
  const { status, priority } = filters;
  
  return useQuery({
    queryKey: ['leads', 'count', status, priority],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Authentication required');

      let query = supabase
        .from('lead_master')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Apply filters with type validation
      if (status && isValidLeadStatus(status)) {
        query = query.eq('status', status);
      }
      if (priority && isValidLeadPriority(priority)) {
        query = query.eq('priority', priority);
      }

      const { count, error } = await query;
      if (error) throw error;

      return count || 0;
    },
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
  });
};
